/**
 * Per-level deck loader. Dynamic imports keep each level's bundle separate
 * (Vite code-splits on dynamic import boundaries) so users only pay for
 * the level they're studying.
 *
 * v1: hand-coded starter decks per level (~10-15 cards each) so the app
 * is demoable end-to-end before the content pipeline runs. The full
 * NGSL+NAWL+Wiktionary ingest replaces these with thousands of cards
 * once `npm run rebuild-content` runs against fetched sources.
 *
 * The phrasal-verb deck (CC BY 4.0 from PHaVE) is merged in at load time
 * filtered by matching CEFR level — so A1 gets only A1-tagged phrasal
 * verbs, A2 gets A1+A2, etc. This way one deck contains both base
 * vocabulary AND tag-filtered subsets, and the English-specific quiz
 * modes (PhrasalVerbQuiz, IrregularVerbQuiz) find their cards in the
 * regular deck.
 *
 * Sidecar translations: per-language JSON files under `./translations/`
 * are loaded on demand and merged into `card.wordTranslations[lang]`.
 * Each file is code-split by Vite (import.meta.glob) so only the active
 * learner language is fetched. The cache prevents duplicate network
 * requests within a session.
 */

import type { EnglishCard } from '../types.js';
import type { EnglishLevel, CEFRLevel } from '../types.js';
import { phrasalVerbs } from './phrasalVerbs.js';
import { learnerLanguage } from '../i18n/learner.js';
import type { L1Code, WordTranslation } from '../i18n/types.js';

// ---------------------------------------------------------------------------
// Sidecar translation loader
// ---------------------------------------------------------------------------

// import.meta.glob is resolved statically by Vite at build time; each matched
// file becomes a separate chunk (lazy). The glob returns {} when the directory
// doesn't exist yet or contains no .json files — no runtime error.
const sidecarLoaders = import.meta.glob<{ default: Record<string, WordTranslation> }>(
  './translations/*.json'
);

const sidecarCache = new Map<L1Code, Record<string, WordTranslation>>();

async function loadSidecar(lang: L1Code): Promise<Record<string, WordTranslation>> {
  const cached = sidecarCache.get(lang);
  if (cached) return cached;
  const loader = sidecarLoaders[`./translations/${lang}.json`];
  const data = loader ? (await loader()).default : {};
  sidecarCache.set(lang, data);
  return data;
}

const CEFR_RANK: Record<CEFRLevel, number> = {
  A1: 1,
  A2: 2,
  B1: 3,
  B2: 4,
  C1: 5,
  C2: 6
};

/** Returns true if `cardCefr` is at or below `targetCefr`. */
function isCefrAtOrBelow(cardCefr: CEFRLevel, targetCefr: CEFRLevel): boolean {
  return CEFR_RANK[cardCefr] <= CEFR_RANK[targetCefr];
}

async function loadBaseDeck(level: EnglishLevel): Promise<EnglishCard[]> {
  switch (level) {
    case 'A1': {
      const mod = await import('./vocabularyA1.js');
      return mod.default;
    }
    case 'A2': {
      const mod = await import('./vocabularyA2.js');
      return mod.default;
    }
    case 'B1': {
      const mod = await import('./vocabularyB1.js');
      return mod.default;
    }
    case 'B2': {
      const mod = await import('./vocabularyB2.js');
      return mod.default;
    }
    case 'C1': {
      const mod = await import('./vocabularyC1.js');
      return mod.default;
    }
    case 'C2': {
      const mod = await import('./vocabularyC2.js');
      return mod.default;
    }
    case 'Everyday': {
      const mod = await import('./vocabularyEveryday.js');
      return mod.default;
    }
  }
}

export async function loadDeck(
  level: EnglishLevel,
  lang: L1Code = learnerLanguage.value
): Promise<EnglishCard[]> {
  const base = await loadBaseDeck(level);

  // Merge in phrasal verbs at or below this CEFR. Everyday gets all of them.
  const phrasalsForLevel: EnglishCard[] =
    level === 'Everyday'
      ? phrasalVerbs
      : phrasalVerbs.filter(pv => isCefrAtOrBelow(pv.cefr, level));

  // Dedupe by id across vocab + phrasal sources. Assert uniqueness inline so a
  // duplicate id (which would make sidecar lookups ambiguous) is reported with
  // its source list — easier to debug than catching it after sidecar merge.
  const seen = new Set<string>();
  const merged: EnglishCard[] = [];
  for (const card of base) {
    if (seen.has(card.id)) {
      throw new Error(`Duplicate card.id "${card.id}" within base vocab — sidecar lookups would be ambiguous.`);
    }
    seen.add(card.id);
    merged.push(card);
  }
  for (const card of phrasalsForLevel) {
    if (seen.has(card.id)) {
      throw new Error(`Duplicate card.id "${card.id}" — phrasal verb collides with base vocab.`);
    }
    seen.add(card.id);
    merged.push(card);
  }

  // Merge per-language sidecar translations into card.wordTranslations.
  // Clone each card to avoid mutating the imported module-level objects;
  // a subsequent loadDeck for a different language must not see prior leftovers.
  const translations = await loadSidecar(lang);
  return merged.map(card => {
    const t = translations[card.id];
    if (!t) return card;
    return {
      ...card,
      wordTranslations: { ...(card.wordTranslations ?? {}), [lang]: t },
    };
  });
}
